import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Check, ChevronDown } from 'lucide-react';

/**
 * SearchSelect - Searchable single-select dropdown
 * Used for FK fields and lookup categories with large option sets.
 * Similar to filter dropdown but for single selection.
 *
 * Props:
 * - value: Current selected value (id for FK, string for lookup)
 * - displayValue: Display text for current selection
 * - options: Array of { id, display } for FK or strings for lookup
 * - onChange: (value) => void
 * - placeholder: Placeholder text when nothing selected
 * - disabled: Disable the control
 * - isLookup: true if options are strings (lookup category), false for FK objects
 */
const SearchSelect = ({
  value,
  displayValue,
  options = [],
  onChange,
  placeholder = '-- Select --',
  disabled = false,
  isLookup = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, openAbove: false });
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsRef = useRef(null);
  const selectedOptionRef = useRef(null);
  const dropdownId = useRef(`searchselect-${Math.random().toString(36).substr(2, 9)}`).current;

  // Normalize options to { id, display } format
  const normalizedOptions = isLookup
    ? options.map(opt => ({ id: opt, display: opt }))
    : options;

  // Filter options by search
  const filteredOptions = normalizedOptions.filter(opt =>
    String(opt.display || '').toLowerCase().includes(search.toLowerCase())
  );

  // Calculate dropdown position
  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const dropdownHeight = 280; // Approximate max height
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    setPosition({
      top: openAbove ? rect.top - dropdownHeight : rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 200),
      openAbove
    });
  }, []);

  // Open dropdown
  const handleOpen = () => {
    if (disabled) return;
    calculatePosition();
    setIsOpen(true);
    setSearch('');
  };

  // Close dropdown
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);

  // Select option
  const handleSelect = (opt) => {
    const newValue = isLookup ? opt.display : opt.id;
    console.log('SearchSelect handleSelect:', { opt, newValue, isLookup });
    onChange(newValue);
    handleClose();
  };

  // Clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    onChange(isLookup ? '' : null);
  };

  // Focus search input and scroll to selected item when opened
  useEffect(() => {
    if (isOpen) {
      // Focus search input
      setTimeout(() => searchInputRef.current?.focus(), 0);
      // Scroll to selected option
      setTimeout(() => {
        if (selectedOptionRef.current && optionsRef.current) {
          selectedOptionRef.current.scrollIntoView({ block: 'center' });
        }
      }, 10);
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Recalculate position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    const handleReposition = () => calculatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, calculatePosition]);

  const hasValue = value !== null && value !== undefined && value !== '';
  const currentDisplay = displayValue || (hasValue ? String(value) : '');

  return (
    <>
      {/* Trigger */}
      <div
        ref={containerRef}
        className={`searchselect-trigger ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''} ${className}`}
        onClick={handleOpen}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={dropdownId}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen();
          }
        }}
      >
        <span className={`searchselect-value ${!hasValue ? 'placeholder' : ''}`}>
          {hasValue ? currentDisplay : placeholder}
        </span>
        <ChevronDown size={14} className="searchselect-chevron" />
      </div>

      {/* Dropdown (portal to body for proper positioning) */}
      {isOpen && (
        <div
          ref={dropdownRef}
          id={dropdownId}
          role="listbox"
          className={`searchselect-dropdown ${position.openAbove ? 'open-above' : ''}`}
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: position.width,
            zIndex: 9999
          }}
        >
          {/* Search input */}
          <div className="searchselect-search">
            <Search size={14} className="searchselect-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="searchselect-search-input"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options list */}
          <div className="searchselect-options" ref={optionsRef}>
            {/* Clear option */}
            {hasValue && (
              <div
                className="searchselect-option searchselect-clear"
                onClick={handleClear}
              >
                <span className="searchselect-option-check" />
                <span className="searchselect-option-text">(Clear selection)</span>
              </div>
            )}

            {filteredOptions.length === 0 ? (
              <div className="searchselect-empty">No matches found</div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = isLookup
                  ? value === opt.display
                  : value === opt.id;
                return (
                  <div
                    key={opt.id}
                    ref={isSelected ? selectedOptionRef : null}
                    className={`searchselect-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelect(opt)}
                  >
                    <span className="searchselect-option-check">
                      {isSelected && <Check size={14} />}
                    </span>
                    <span className="searchselect-option-text">{opt.display}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SearchSelect;
